import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../api/axiosInstance";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";
import LoadingSpinner from "./LoadingSpinner";
import "datatables.net-dt/css/jquery.dataTables.css";
import $ from "jquery";
import "datatables.net";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { usePlan } from "../context/PlanContext";
import UpgradePrompt from "./UpgradePrompt";
import subscriptionService from "../services/subscriptionService";
import paymentManagementService, { PAYMENT_TYPE } from "../services/paymentManagementService";





function ProductManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { planInfo, hasFeature, isReadOnly } = usePlan();

  const [products, setProducts] = useState([]);
  const [storeInfo, setStoreInfo] = useState(null);
  const [dataTable, setDataTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [soldQuantityMap, setSoldQuantityMap] = useState({});

  // Subscription states
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);



  // Filters
  const [stockFilter, setStockFilter] = useState("all"); // all, in_stock, low_stock, out_of_stock
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, inactive

  const [startDate, setStartDate] = useState(null);  // filter start date
  const [endDate, setEndDate] = useState(null);      // filter end date
  const [topSellingProducts, setTopSellingProducts] = useState([]);

  // add near other state declarations
  const [revenueData, setRevenueData] = useState({
    total_revenue: 0,
    total_orders: 0,
    by_payment_type: {}, // optional breakdown if you want later
  });


  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };



  const savedPageLength = localStorage.getItem("productManagement_pageLength") || 10;

  // Modals state
  const [deleteId, setDeleteId] = useState("");
  const [deleteName, setDeleteName] = useState("");
  const [updateStockId, setUpdateStockId] = useState("");
  const [updateStockName, setUpdateStockName] = useState("");
  const [updateQuantity, setUpdateQuantity] = useState("");
  const [updateMode, setUpdateMode] = useState("set"); // set or add

  // Fetch both APIs
  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      try {
        setLoading(true);
        const businessId = localStorage.getItem("business_id");
        const params = businessId ? { business_id: businessId } : {};

        // parallel fetch
        const [giftResp, storeResp] = await Promise.all([
          api.get('/gift/list', { params }),
          api.get('/business/store/info', { params }),
        ]);

        if (!mounted) return;

        if (giftResp.data?.status === "success") {
          // gift/list returns result array
          setProducts(giftResp.data.result || []);
        } else {
          setProducts([]);
        }

        if (storeResp.data?.status === "success") {
          setStoreInfo(storeResp.data.result || null);
        } else {
          setStoreInfo(null);
        }
      } catch (err) {
        console.error("Error loading product management data:", err);
        toast.error("Failed to load product data.");
      } finally {
        setLoading(false);
      }
    };



    fetchAll();
    fetchRevenueData();
    fetchTopSellingProducts();

    // Fetch subscription and payment info
    const business_id = localStorage.getItem("business_id");
    if (business_id) {
      fetchSubscriptionInfo(business_id);
      fetchPaymentInfo(business_id);
    }

    return () => {
      mounted = false;
    };
  }, []);

  // Prepare DataTable when products or filters change
  useEffect(() => {
    // compute filtered rows
    const filtered = products
      .map((p) => {
        const sold = Number(soldQuantityMap[p._id] || p.sold_quantity || 0);
        const qty = Number(p.quantity || 0);
        const remaining = qty - sold;
        return {
          ...p,
          remaining,
          sold: sold,
          displayCategory: (p.category_details && p.category_details[0]?.category) || "—",
        };
      })
      .filter((p) => {
        // stock filter
        if (stockFilter === "in_stock" && p.remaining <= 0) return false;
        if (stockFilter === "low_stock" && p.remaining >= 5) return false;
        if (stockFilter === "out_of_stock" && p.remaining > 0) return false;

        // status filter
        if (statusFilter !== "all" && (p.status || "").toLowerCase() !== statusFilter) return false;

        // category filter
        if (categoryFilter !== "all" && p.displayCategory !== categoryFilter) return false;

        // if (startDate && new Date(p.created_date) < startDate) return false;
        // if (endDate && new Date(p.created_date) > endDate) return false;
        // Date range filter - FIXED VERSION (Timezone-aware)
        if (startDate || endDate) {
          // Parse the product's created_date and convert to local date string
          const productUTCDate = new Date(p.created_date);
          const productDateString = getLocalDateString(productUTCDate);

          // Check start date
          if (startDate) {
            const startDateString = getLocalDateString(startDate);
            if (productDateString < startDateString) return false;
          }

          // Check end date
          if (endDate) {
            const endDateString = getLocalDateString(endDate);
            if (productDateString > endDateString) return false;
          }
        }


        return true;
      });

    // Destroy existing table
    if ($.fn.DataTable.isDataTable("#productManagement")) {
      $("#productManagement").DataTable().clear().destroy();
      $("#productManagement").off("click", ".view-btn");
      $("#productManagement").off("click", ".edit-btn");
      $("#productManagement").off("click", ".delete-btn");
      $("#productManagement").off("click", ".stock-btn");
    }

    // initialize DataTable
    const table = $("#productManagement").DataTable({
      data: filtered,
      columns: [
        {
          data: "url",
          title: t("table.product"),
          render: (data) => `<div class="prodlist_img"><img src="${data || ''}" alt="Product" style="width:48px;height:48px;object-fit:cover;border-radius:4px;" /></div>`
        },
        { data: "name", title: t("table.title") },
        { data: "displayCategory", title: t("table.category") },
        { data: "price", title: t("table.price"), render: (d) => `₹${d || 0}` },
        {
          data: "created_date", title: t("table.addedOn"),
          render: (data) => new Date(data).toLocaleString(),
        },
        { 
          data: "quantity", 
          title: t("table.quantity"),
          render: (data, type, row) => {
            // Check if it's a digital product (quantity is 999999 or is_digital flag is true)
            if (row.is_digital || data === 999999) {
              return '<span class="badge badge-success fw-semibold">Unlimited (Digital Product)</span>';
            }
            return data || 0;
          }
        },

        {
          data: "sold",
          title: t("table.sold"),
          render: (d) => d || 0
        },

        {
          data: "status",
          title: t("table.status"),
          render: (d) => `<span class="badge ${d === "active" ? "badge-success" : "badge-secondary"}">${d || "—"}</span>`
        },
        {
          data: null,
          title: t("table.action"),
          orderable: false,
          render: (data) => {
            const id = data._id;
            return `
              <div class="text-center">
               
                <button class="btn btn-sm btn-primary edit-btn" data-id="${id}" title="Edit"><i class="fa-solid fa-pen"></i></button>
                
                <button class="btn btn-sm btn-danger delete-btn" data-id="${id}" data-name="${data.name}" title="Delete"><i class="fa-solid fa-trash"></i></button>
              </div>
            `;
          },
        },
      ],
      destroy: true,
      responsive: true,
      autoWidth: false,
      paging: true,
      searching: true,
      lengthChange: true,
      pageLength: parseInt(savedPageLength, 10),
      lengthMenu: [[10, 25, 50, 100, -1], [10, 25, 50, 100]],
      language: {
        emptyTable: t("product.noData", "No products found"),
      },
      drawCallback: function (settings) {
        localStorage.setItem("productManagement_pageLength", settings._iDisplayLength);
      }
    });

    setDataTable(table);

    // Attach listeners
    $("#productManagement").on("click", ".view-btn", function () {
      const id = $(this).data("id");
      navigate(`/gift_view?id=${id}`);
    });

    $("#productManagement").on("click", ".edit-btn", function () {
      const id = $(this).data("id");
      navigate(`/gift_edit?id=${id}`);
    });

    $("#productManagement").on("click", ".delete-btn", function () {
      const id = $(this).data("id");
      const name = $(this).data("name");
      openDeleteModal(id, name);
    });

    $("#productManagement").on("click", ".stock-btn", function () {
      const id = $(this).data("id");
      const name = $(this).data("name");
      openUpdateStockModal(id, name);
    });

    return () => {
      if ($.fn.DataTable.isDataTable("#productManagement")) {
        $("#productManagement").DataTable().clear().destroy();
      }
      $("#productManagement").off("click", ".view-btn");
      $("#productManagement").off("click", ".edit-btn");
      $("#productManagement").off("click", ".delete-btn");
      $("#productManagement").off("click", ".stock-btn");
    };
  }, [products, stockFilter, categoryFilter, statusFilter, startDate, endDate
    , t, navigate]);


  // Add this new useEffect after the existing one
  useEffect(() => {
    if (products.length > 0) {
      fetchTopSellingProducts();
    }
  }, [products]); // Re-fetch when products are loaded

  // Fetch subscription info
  const fetchSubscriptionInfo = async (businessId) => {
    try {
      const subInfo = await subscriptionService.getSubscriptionInfo(businessId);
      setSubscriptionInfo(subInfo);
      console.log('[PRODUCT_MGMT] Subscription info loaded:', subInfo);
    } catch (error) {
      console.error('[PRODUCT_MGMT] Error fetching subscription:', error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  // Fetch payment management info
  const fetchPaymentInfo = async (businessId) => {
    try {
      const info = await paymentManagementService.getPaymentInfo(businessId);
      setPaymentInfo(info);
      console.log('[PRODUCT_MGMT] Payment info loaded:', info);
    } catch (error) {
      console.error('[PRODUCT_MGMT] Error fetching payment info:', error);
    }
  };

  // Check if feature is available (supports both manual pay and subscription)
  const checkFeatureAccess = (feature) => {
    // Priority 1: Check if user has active subscription
    if (paymentInfo && paymentInfo.paymentType === PAYMENT_TYPE.AUTO_RENEW && subscriptionInfo) {
      return subscriptionService.hasFeature(subscriptionInfo, feature);
    }

    // Priority 2: Fall back to manual plan check
    if (planInfo) {
      return hasFeature(feature);
    }

    // No payment method
    return false;
  };

  // Check if user is in read-only mode (supports both manual pay and subscription)
  const checkReadOnlyMode = () => {
    // Priority 1: Check subscription
    if (paymentInfo && paymentInfo.paymentType === PAYMENT_TYPE.AUTO_RENEW && subscriptionInfo) {
      return subscriptionInfo.isReadOnly || !subscriptionInfo.hasActivePlan;
    }

    // Priority 2: Fall back to manual plan
    return isReadOnly;
  };

  // Helpers
  const openDeleteModal = (id, name) => {
    setDeleteId(id);
    setDeleteName(name);
    setShowDeleteModal(true);

  };


  const fetchRevenueData = async () => {
    try {
      const businessId = localStorage.getItem("business_id");
      const signupStatus = localStorage.getItem("signup");

      let params = {};
      if (businessId && signupStatus === "user") {
        params.business_id = businessId;
      }

      const response = await api.get(
        '/event/business/list',
        { params }
      );

      const events = response.data?.result?.[0]?.events || [];

      let totalRevenue = 0;
      let paidCount = 0;
      let pendingCount = 0;

      events.forEach(event => {
        const status = event.payment_status?.toLowerCase();

        if (status === "paid") {
          paidCount++;

          // Add event total_price
          totalRevenue += Number(event.total_price || 0);

          // Add cart_details purchased_price
          event.cart_details?.forEach(item => {
            totalRevenue += Number(item.purchased_price || 0);
          });

        } else if (status === "pending") {
          pendingCount++;
        }
      });

      setRevenueData({
        total_revenue: totalRevenue,
        paid_orders: paidCount,
        pending_orders: pendingCount,
        total_orders: paidCount + pendingCount
      });

    } catch (error) {
      console.error("Revenue Fetch Error:", error);
    }
  };


  const fetchTopSellingProducts = async () => {
    try {
      const businessId = localStorage.getItem("business_id");
      const signupStatus = localStorage.getItem("signup");

      let params = {};
      if (businessId && signupStatus === "user") {
        params.business_id = businessId;
      }

      const response = await api.get(
        '/event/business/list',
        { params }
      );

      const events = response.data?.result?.[0]?.events || [];

      // Extract all products from paid orders
      const productSales = {};

      events.forEach(event => {
        // Only count paid orders
        if (event.payment_status?.toLowerCase() === "paid" &&
          event.purchase_status?.toLowerCase() === "paid") {

          event.cart_details?.forEach(item => {
            const giftId = item.gift?.gift_id;

            if (giftId) {
              if (!productSales[giftId]) {
                productSales[giftId] = {
                  _id: giftId,
                  name: item.gift.name,
                  price: item.gift.price,
                  url: item.gift.url,
                  business_id: item.gift.business_id,
                  sold_quantity: 0,
                  total_revenue: 0,
                  category_details: products.find(p => p._id === giftId)?.category_details || []
                };
              }

              // Add the order count
              productSales[giftId].sold_quantity += Number(item.order_data_count || 0);
              productSales[giftId].total_revenue += Number(item.purchased_price || 0);
            }
          });
        }
      });

      // Convert to array and sort by sold_quantity
      const sortedProducts = Object.values(productSales)
        .sort((a, b) => b.sold_quantity - a.sold_quantity)
        .slice(0, 10); // Top 10 products

      setTopSellingProducts(sortedProducts);

      // Create a map: { productId: sold_quantity }
      const soldMap = {};
      sortedProducts.forEach(p => {
        soldMap[p._id] = p.sold_quantity;
      });
      setSoldQuantityMap(soldMap);


    } catch (error) {
      console.error("Top Selling Products Fetch Error:", error);
      setTopSellingProducts([]);
    }
  };



  const confirmDelete = async () => {
    try {
      const businessId = localStorage.getItem("business_id");

      const payload = {
        gift_id: deleteId,
        gift_status: "delete",
        business_id: businessId,
      };

      const res = await api.post(
        '/gift/delete',
        payload
      );

      if (res.data?.status === "success") {
        toast.success("Product deleted successfully");

        // Remove deleted item from table
        setProducts((prev) => prev.filter((p) => p._id !== deleteId));

        // Close modal
        setShowDeleteModal(false);
      } else {
        toast.error("Delete failed");
      }
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error("Something went wrong");
    }
  };

  const openUpdateStockModal = (id, name) => {
    setUpdateStockId(id);
    setUpdateStockName(name);
    setUpdateQuantity("");
    setUpdateMode("set");
    $("#pm_modal_update_stock").modal("show");
  };

  const submitUpdateStock = async () => {
    if (!updateQuantity || isNaN(Number(updateQuantity))) {
      toast.error("Enter valid quantity");
      return;
    }

    try {
      // Your backend might expect different payload - adjust if needed.
      // Here we send gift_id and new quantity or delta based on mode.
      const businessId = localStorage.getItem("business_id");
      let payload;
      if (updateMode === "set") {
        payload = { gift_id: updateStockId, quantity: Number(updateQuantity) };
      } else {
        // add mode = add to existing quantity
        payload = { gift_id: updateStockId, add_quantity: Number(updateQuantity) };
      }
      if (businessId) payload.business_id = businessId;

      const res = await api.post('/gift/update', payload);
      if (res.data?.status === "success") {
        toast.success("Stock updated");
        // Update local state
        setProducts((prev) =>
          prev.map((p) =>
            p._id === updateStockId
              ? {
                ...p,
                quantity:
                  updateMode === "set"
                    ? Number(updateQuantity)
                    : Number((p.quantity || 0) + Number(updateQuantity)),
              }
              : p
          )
        );
        $("#pm_modal_update_stock").modal("hide");
      } else {
        toast.error("Failed to update stock");
      }
    } catch (err) {
      console.error("Update stock error:", err);
      toast.error("Update failed.");
    }
  };

  // Category list derived from storeInfo
  const categories = (storeInfo?.store_settings?.category_section?.selected_categories || []).map(
    (c) => c.category
  );


  // Top Selling Products (Top 10)



  // Render loader OR main page
  if (loading) {
    return <LoadingSpinner message="Loading Product Analytics..." />;
  }

  return (
    <div className="wrapper" id="root">
      <header>
        <NavigationBar />
      </header>

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0 txt_lft">{t('navigation.productinventoryhub')}</h1>
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">

            {/* Top Selling Products Section - Premium Only */}
            {checkFeatureAccess('premiumAnalytics') && (
              <div className="row mb-4">
                <div className="col-lg-12">
                  <div
                    className="card border-0 shadow-lg"
                    style={{ borderRadius: "14px", overflow: "hidden" }}
                  >
                    {/* HEADER */}
                    <div
                      className="card-header"
                      style={{
                        background: "linear-gradient(135deg, #1E5B3A 0%, #2E7D4F 100%)",
                        color: "#fff",
                        padding: "18px 22px",
                      }}
                    >
                      <h5 className="mb-0 fw-bold">Top Selling Products</h5>
                    </div>

                  <div className="card-body p-4">

                    {/* EMPTY STATE */}
                    {topSellingProducts.length === 0 ? (
                      <div className="text-center py-5">
                        <i
                          className="fas fa-box-open"
                          style={{ fontSize: "64px", color: "#ccc", marginBottom: "16px" }}
                        ></i>
                        <h5 className="fw-bold text-muted">No Products Sold Yet</h5>
                        <p className="text-muted mb-0">
                          Top selling products will appear here once orders are completed.
                        </p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table
                          className="table table-bordered align-middle"
                          style={{ borderRadius: "14px", overflow: "hidden" }}
                        >
                          <thead
                            style={{
                              background: "#FDF9F2",
                              fontWeight: "600",
                              letterSpacing: "0.3px",
                            }}
                          >
                            <tr>
                              <th>Rank</th>
                              <th>Product</th>
                              <th>Product Name</th>
                              <th>Category</th>
                              <th>Price</th>
                              <th>Sold</th>
                              <th>Revenue</th>
                            </tr>
                          </thead>

                          <tbody>
                            {topSellingProducts.map((item, index) => (
                              <tr key={item._id} className="product-row">
                                <td>
                                  <span
                                    className={`badge ${index === 0 ? 'bg-warning' :
                                      index === 1 ? 'bg-secondary' :
                                        index === 2 ? 'bg-info' :
                                          'bg-light text-dark'
                                      } px-3 py-2`}
                                    style={{ fontSize: "14px", fontWeight: "bold" }}
                                  >
                                    #{index + 1}
                                  </span>
                                </td>
                                <td>
                                  <img
                                    src={item.url}
                                    alt="product"
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      objectFit: "cover",
                                      borderRadius: "6px",
                                      border: "2px solid #E7DCC6"
                                    }}
                                  />
                                </td>
                                <td className="fw-semibold">{item.name}</td>
                                <td>
                                  <span className="fw-semibold">
                                    {item.category_details?.[0]?.category || "-"}
                                  </span>
                                </td>
                                <td className="fw-bold">₹{item.price?.toLocaleString() || 0}</td>
                                <td>
                                  <span className="badge bg-success px-3 py-2" style={{ fontSize: "13px" }}>
                                    {item.sold_quantity} units
                                  </span>
                                </td>
                                <td className="fw-bold text-success">
                                  ₹{item.total_revenue?.toLocaleString() || 0}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Show upgrade prompt for Pro users (not Premium) */}
            {checkFeatureAccess('advancedAnalytics') && !checkFeatureAccess('premiumAnalytics') && (
              <div className="row mb-4">
                <div className="col-lg-12">
                  <UpgradePrompt 
                    message="Upgrade to Premium to see Top Selling Products and advanced insights" 
                    inline 
                  />
                </div>
              </div>
            )}

            {/* Hover Effect */}
            <style>
              {`
    .product-row {
      transition: all 0.2s ease-in-out;
    }
    
    .product-row:hover {
      background: #FDF9F2 !important;
      transform: scale(1.01);
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    }
    
    .table-responsive {
      border-radius: 14px;
    }
    
    .table thead th {
      border-bottom: 2px solid #dee2e6;
      padding: 12px;
      text-align: center;
    }
    
    .table tbody td {
      vertical-align: middle;
      padding: 12px;
      text-align: center;
    }
  `}
            </style>


            {/* Total Revenue Section - Basic Analytics (All Plans) */}
            {!checkReadOnlyMode() && (
              <div className="row mb-4">
                <div className="col-lg-12">
                  <div
                    className="card border-0 shadow-lg"
                    style={{
                      borderRadius: "14px",
                      overflow: "hidden",
                    }}
                  >
                    {/* HEADER */}
                    <div
                      className="card-header"
                      style={{
                        background: "linear-gradient(135deg, #1E5B3A 0%, #2E7D4F 100%)",
                        color: "#fff",
                        padding: "18px 22px",
                      }}
                    >
                      <h5 className="mb-0 fw-bold" style={{ letterSpacing: "0.4px" }}>
                        Total Revenue Overview
                      </h5>
                    </div>

                    <div className="card-body p-4">

                    {/* NO REVENUE YET */}
                    {revenueData.total_orders === 0 ? (
                      <div className="text-center py-5">
                        {/* <img
                          src="/assets/img/empty-revenue.svg"
                          alt="No data"
                          style={{ width: "140px", opacity: 0.7 }}
                        /> */}
                        <h5 className="mt-3 fw-bold">No Revenue Yet</h5>
                        <p className="text-muted mb-0">
                          Revenue details will appear once orders are received.
                        </p>
                      </div>
                    ) : (
                      <div className="row g-4">

                        {/* TOTAL REVENUE */}
                        <div className="col-md-3 col-6">
                          <div className="p-3 rounded shadow-sm revenue-box h-100 text-center"
                            style={{
                              background: "linear-gradient(135deg, #E1EFE3, #F1F7F1)",
                              borderRadius: "14px",
                              transition: "0.3s",
                            }}
                          >
                            <div className="icon mb-2" style={{ fontSize: "26px" }}></div>
                            <h4 className="fw-bold">₹{revenueData.total_revenue.toLocaleString()}</h4>
                            <p className="text-muted mb-0">Total Revenue</p>
                          </div>
                        </div>

                        {/* PAID ORDERS */}
                        <div className="col-md-3 col-6">
                          <div className="p-3 rounded shadow-sm revenue-box h-100 text-center"
                            style={{
                              background: "linear-gradient(135deg, #F3E9D8, #FAF3E7)",
                              borderRadius: "14px",
                              transition: "0.3s",
                            }}
                          >
                            <div className="icon mb-2" style={{ fontSize: "26px" }}></div>
                            <h4 className="fw-bold">{revenueData.paid_orders}</h4>
                            <p className="text-muted mb-0">Paid Orders</p>
                          </div>
                        </div>

                        {/* PENDING ORDERS */}
                        <div className="col-md-3 col-6">
                          <div className="p-3 rounded shadow-sm revenue-box h-100 text-center"
                            style={{
                              background: "linear-gradient(135deg, #FCE3C6, #FFF4E6)",
                              borderRadius: "14px",
                              transition: "0.3s",
                            }}
                          >
                            <div className="icon mb-2" style={{ fontSize: "26px" }}></div>
                            <h4 className="fw-bold">{revenueData.pending_orders}</h4>
                            <p className="text-muted mb-0">Pending Orders</p>
                          </div>
                        </div>

                        {/* ACTION BUTTON */}
                        <div className="col-md-3 col-6">
                          <div className="d-flex align-items-center justify-content-center h-100">
                            <button
                              className="btn btn-lg btn-primary fw-semibold shadow-sm"
                              style={{
                                borderRadius: "12px",
                                padding: "10px 18px",
                              }}
                              onClick={() => navigate("/ordermanagement")}
                            >
                              View Orders →
                            </button>
                          </div>
                        </div>

                      </div>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Show message for expired/basic plan users */}
            {checkReadOnlyMode() && (
              <div className="row mb-4">
                <div className="col-lg-12">
                  <UpgradePrompt message="Your plan has expired. Upgrade to access analytics and manage your products." />
                </div>
              </div>
            )}

            {/* Hover Effect */}
            <style>
              {`
    .revenue-box:hover {
      transform: translateY(-4px);
      box-shadow: 0 6px 18px rgba(0,0,0,0.1);
    }
  `}
            </style>





            {/* Filters row - Pro and Premium only */}
            {checkFeatureAccess('advancedAnalytics') && (
              <div className="row mb-3">
                <div className="col-lg-12 d-flex gap-3 flex-wrap" style={{ gap: "18px" }}>
                  {/* Stock filters */}
                  <select
                    className="form-select w-auto"
                    style={{
                      color: "#fff",
                      backgroundColor: "#1E5B3A",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      minWidth: "160px"
                    }}
                    value={stockFilter}
                    onChange={(e) => setStockFilter(e.target.value)}
                  >
                    <option value="all" style={{ color: "#000", backgroundColor: "#fff" }}>
                      All Stocks
                    </option>
                    <option value="in_stock" style={{ color: "#000", backgroundColor: "#fff" }}>
                      In Stock
                    </option>
                    <option value="low_stock" style={{ color: "#000", backgroundColor: "#fff" }}>
                      Low Stock
                    </option>
                    <option value="out_of_stock" style={{ color: "#000", backgroundColor: "#fff" }}>
                      Out of Stock
                    </option>
                  </select>


                  <select
                    className="form-select w-auto"
                    style={{
                      color: "#fff",                   // text color white
                      backgroundColor: "#2E7D4F",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      minWidth: "180px"
                    }}
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="all" style={{ color: "#000", backgroundColor: "#fff" }}>
                      All Categories
                    </option>

                    {Array.from(
                      new Set(
                        products
                          .map((p) => p.category_details?.[0]?.category)
                          .filter((c) => c && c !== "—" && c !== undefined)
                      )
                    ).map((cat) => (
                      <option key={cat} value={cat} style={{ color: "#000", backgroundColor: "#fff" }}>
                        {cat}
                      </option>
                    ))}
                  </select>




                  {/* Status filter */}
                  <select
                    className="form-select w-auto"
                    style={{
                      color: "#fff",                   // dropdown text color
                      backgroundColor: "#B9500A",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      minWidth: "160px"
                    }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all" style={{ color: "#000", backgroundColor: "#fff" }}>
                      All Status
                    </option>
                    <option value="active" style={{ color: "#000", backgroundColor: "#fff" }}>
                      Active
                    </option>
                    <option value="inactive" style={{ color: "#000", backgroundColor: "#fff" }}>
                      Inactive
                    </option>
                  </select>

                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      placeholderText="Start Date"
                      className="form-control"
                      style={{
                        minWidth: "160px",
                        borderRadius: "6px",
                        padding: "6px 12px",
                      }}
                    />
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      placeholderText="End Date"
                      className="form-control"
                      style={{
                        minWidth: "160px",
                        borderRadius: "6px",
                        padding: "6px 12px",
                      }}
                    />
                  </div>

                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setStartDate(null);
                      setEndDate(null);
                      setStockFilter("all");
                      setCategoryFilter("all");
                      setStatusFilter("all");

                      toast.info(t('navigation.filterscleared'));

                    }}
                  >
                    Clear
                  </button>




                  {/* Add product shortcut */}
                  <div style={{ marginLeft: "auto" }}>
                    <button className="btn btn-primary" onClick={() => navigate("/gift_add")}>
                      <i className="fa-solid fa-square-plus mr-2" /> Add Product
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Show upgrade prompt for Starter/Free Trial users */}
            {!checkFeatureAccess('advancedAnalytics') && !checkReadOnlyMode() && (
              <div className="row mb-3">
                <div className="col-lg-12">
                  <UpgradePrompt 
                    message="Upgrade to Pro or Premium to access advanced filters and analytics" 
                    inline 
                  />
                </div>
              </div>
            )}

            {/* Table */}
            <div className="row">
              <div className="col-lg-12">
                <div className="table-responsive">
                  <table id="productManagement" className="table table-striped table-bordered txt_lft" style={{ width: "100%" }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Delete Modal */}
      {/* React Delete Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "8px",
              width: "350px",
              textAlign: "center"
            }}
          >
            <h4>Delete Product</h4>
            <p>Are you sure you want to delete <b>{deleteName}</b>?</p>

            <div className="d-flex  justify-content-center mt-3" style={{ gap: "20px" }}>
              <button className="btn btn-danger" onClick={confirmDelete}>
                Yes, Delete
              </button>
              <button
                className="btn btn-secondary ms-3"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Update Stock Modal */}
      <div className="modal fade" id="pm_modal_update_stock" tabIndex={-1} aria-labelledby="pm_modal_update_stock_label" aria-hidden="true" data-backdrop="static">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="pm_modal_update_stock_label">Update Stock - {updateStockName}</h5>
              <button type="button" className="close" data-dismiss="modal" aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              <div className="mb-2">
                <label className="me-2">Mode:</label>
                <div className="btn-group" role="group">
                  <input type="radio" id="mode_set" name="update_mode" checked={updateMode === "set"} onChange={() => setUpdateMode("set")} />
                  <label htmlFor="mode_set" className="mx-2">Set Quantity</label>
                  <input type="radio" id="mode_add" name="update_mode" checked={updateMode === "add"} onChange={() => setUpdateMode("add")} />
                  <label htmlFor="mode_add" className="mx-2">Add Quantity</label>
                </div>
              </div>
              <div className="mb-3">
                <label>Quantity</label>
                <input type="number" className="form-control" value={updateQuantity} onChange={(e) => setUpdateQuantity(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button type="button" className="btn btn-primary" onClick={submitUpdateStock}>Save</button>
              <button type="button" className="btn btn-secondary" data-dismiss="modal">Cancel</button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default ProductManagement;
